/**
 * Retiro en tienda en el carrito: linea por producto y aviso del resumen.
 * El stock se consulta a /products/handle?view=iurop-pickup&variant=ID (stock real de Shopify).
 * Los textos y el filtro de sucursal salen de la configuracion del tema (grupo "Retiro en tienda").
 */
(() => {
  if (customElements.get('iurop-pickup-line')) return;

  const TTL = 5 * 60 * 1000;
  const memory = new Map();
  const SVG_NS = 'http://www.w3.org/2000/svg';

  function icon(size) {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', 'iurop-pickup__svg');
    svg.setAttribute('width', size);
    svg.setAttribute('height', size);
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.7');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    ['M3 9l1.5-5h15L21 9', 'M3 9a3 3 0 0 0 6 0 3 3 0 0 0 6 0 3 3 0 0 0 6 0', 'M5 12v8h14v-8', 'M10 20v-5h4v5'].forEach((d) => {
      const path = document.createElementNS(SVG_NS, 'path');
      path.setAttribute('d', d);
      svg.appendChild(path);
    });
    return svg;
  }

  function getPickup(productUrl, variantId) {
    const key = `iurop-pickup:${variantId}`;
    if (memory.has(key)) return memory.get(key);

    const request = (async () => {
      try {
        const cached = JSON.parse(sessionStorage.getItem(key) || 'null');
        if (cached && Date.now() - cached.t < TTL) return cached.d;
      } catch (_e) {}

      const response = await fetch(`${productUrl}?view=iurop-pickup&variant=${encodeURIComponent(variantId)}`);
      if (!response.ok) throw new Error(`pickup ${response.status}`);
      const data = JSON.parse(await response.text());
      try {
        sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), d: data }));
      } catch (_e) {}
      return data;
    })();

    memory.set(key, request);
    request.catch(() => memory.delete(key));
    return request;
  }

  /** Sustituye {clave} por su valor en un texto configurable. */
  function fill(template, values) {
    return (template || '').replace(/\{(\w+)\}/g, (match, key) => (key in values ? values[key] : match));
  }

  /** Base: pinta desde el estado guardado y se repinta si un morph del carrito vacia el contenido. */
  class IuropPickupBase extends HTMLElement {
    state = null;
    #painting = false;

    connectedCallback() {
      this.observer = new MutationObserver(() => {
        if (this.state && !this.#painting && this.needsPaint()) this.paint();
      });
      this.observer.observe(this, { childList: true, attributes: true, attributeFilter: ['hidden'] });
      this.load().catch(() => {
        this.state = { show: false };
        this.paint();
      });
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    /** Solo repintar si el DOM no coincide con el estado (p. ej. tras un morph del carrito). Evita bucles. */
    needsPaint() {
      return this.state.show ? this.hidden || !this.firstElementChild : !this.hidden || this.childElementCount > 0;
    }

    paint() {
      this.#painting = true;
      const content = this.state?.show ? this.build() : null;
      if (content) this.replaceChildren(...content);
      else if (this.childElementCount > 0) this.replaceChildren();
      if (this.hidden !== !content) this.hidden = !content;
      this.#painting = false;
    }
  }

  class IuropPickupLine extends IuropPickupBase {
    async load() {
      const { variantId, productUrl } = this.dataset;
      const data = await getPickup(productUrl, variantId);
      this.state = { show: data.available && data.stores.length > 0, stores: data.stores };
      this.paint();
    }

    build() {
      const text = document.createElement('span');
      const prefix = (this.dataset.prefix || '').trim();
      if (prefix) text.append(`${prefix} `);
      const strong = document.createElement('strong');
      strong.textContent = this.state.stores[0];
      text.append(strong);
      return [icon(16), text];
    }
  }

  class IuropPickupNote extends IuropPickupBase {
    async load() {
      const items = JSON.parse(this.dataset.items || '[]');
      const results = await Promise.all(items.map((i) => getPickup(i.u, i.v).catch(() => null)));
      const withStock = results.filter((r) => r && r.available && r.stores.length > 0);
      let common = [];
      if (results.length > 0 && withStock.length === results.length) {
        common = withStock[0].stores.filter((name) => withStock.every((r) => r.stores.includes(name)));
      }
      this.state = { show: withStock.length > 0, total: results.length, partial: withStock.length, common };
      this.paint();
    }

    build() {
      const { total, partial, common } = this.state;
      const d = this.dataset;
      const values = { sucursal: common[0] || '', n: String(partial), total: String(total) };
      let title;
      let body;
      if (common.length > 0) {
        title = d.allTitle;
        body = d.allText;
      } else if (partial === total) {
        title = d.splitTitle;
        body = d.splitText;
      } else {
        title = d.someTitle;
        body = d.someText;
      }

      const p = document.createElement('p');
      const strong = document.createElement('strong');
      strong.textContent = fill(title, values);
      p.append(strong, ` ${fill(body, values)}`);
      return [icon(20), p];
    }
  }

  customElements.define('iurop-pickup-line', IuropPickupLine);
  customElements.define('iurop-pickup-note', IuropPickupNote);
})();
